import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, Loader2, Send } from "lucide-react";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { GuardedText } from "@/components/GuardedText";

// Shared comment drawer — used by the feed AND single-video pages.
const CommentDrawer = ({ video, onClose, onCommentPosted }) => {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const { user } = usePlyxAuth();

  useEffect(() => {
    setLoading(true);
    plyxApi.get(`/plyx/videos/${video.video_id}/comments`)
      .then((r) => setComments(r.data))
      .catch(() => toast.error("Could not load comments"))
      .finally(() => setLoading(false));
  }, [video.video_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || !user) return;
    setPosting(true);
    try {
      const { data } = await plyxApi.post(`/plyx/videos/${video.video_id}/comments`, { body: body.trim() });
      setComments((c) => [data, ...c]);
      setBody("");
      onCommentPosted?.(video.video_id);
    } catch {
      toast.error("Could not post comment");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="plyx-comment-drawer"
      style={{ height: "100dvh" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#1a1a1a] text-white rounded-t-2xl sm:rounded-2xl max-h-[85dvh] flex flex-col"
        // Lift the form above the fixed bottom navigation dock
        // (`PlyxBottomNav` is `h-16` ≈ 64 px and `sm:hidden`). Without
        // this padding the "Add a comment…" input is rendered *behind*
        // the dock and creators think they can't comment.
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)" }}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-semibold" data-testid="plyx-comment-count">
            Comments · {loading ? (video.comment_count || 0) : Math.max(comments.length, video.comment_count || 0, 0)}
          </h3>
          <button onClick={onClose} aria-label="Close" className="text-white/60 hover:text-white" data-testid="plyx-comment-close">
            <X className="w-5 h-5"/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-center text-white/60 py-8"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-center text-white/40 text-sm py-8">No comments yet. Be the first.</div>
          ) : comments.map((c) => (
            <div key={c.comment_id} className="flex gap-3" data-testid={`plyx-comment-${c.comment_id}`}>
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-xs font-semibold shrink-0">
                {c.author?.display_name?.slice(0, 1) || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-semibold mr-2">@{c.author?.handle || "user"}</span>
                  <GuardedText text={c.body} className="text-white/80" />
                </div>
                <div className="text-xs text-white/40 mt-1">{c.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
            </div>
          ))}
        </div>
        {user ? (
          <form onSubmit={submit} className="p-3 border-t border-white/10 flex items-center gap-2">
            <input
              type="text"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add a comment…"
              data-testid="plyx-comment-input"
              maxLength={500}
              className="flex-1 px-3 py-2 rounded-full bg-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
            <button
              type="submit"
              disabled={posting || !body.trim()}
              data-testid="plyx-comment-submit"
              className="w-9 h-9 rounded-full flex items-center justify-center text-white disabled:opacity-40"
              style={{ background: "#E85D4E" }}
            >
              {posting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            </button>
          </form>
        ) : (
          <div className="p-4 border-t border-white/10 text-center text-sm text-white/60">
            <Link to="/plyx/login" className="underline hover:text-white">Log in</Link> to comment.
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentDrawer;
